<template>
	<div class="row">
		<div class="columns column3 highlights-nav">
			<div v-for="(category, index) in items" :key="index" class="category">
				<div class="container highlight-category">
					<div
						class="highlight-item button-toggle"
						:class="{ active: activeCategory === parseInt(category.value, 10) }"
						@click="updateMarkers(parseInt(category.value, 10))"
					>
						<span>{{ category.name }}</span>
						<div class="expand-icon dropdown-arrow">
							<font-awesome-icon
								:icon="expanded ? 'fal fa-chevron-up' : 'fal fa-chevron-down'"
								size="1x"
							/>
						</div>
					</div>
					<slide-up-down
						:model-value="activeCategory === parseInt(category.value, 10)"
						class="highlight-content"
						:duration="300"
					>
						<ul>
							<li v-for="item in category.items" :key="item.ID">
								<span :data-id="`#huid_${item.ID}`" @click="openInfowindow($event)">
									{{ item.header }}
								</span>
							</li>
						</ul>
					</slide-up-down>
				</div>
			</div>
		</div>
		<div class="columns column9 maps">
			<div id="gmap" ref="gmap" />
		</div>
	</div>
</template>

<script setup>
import { Loader } from '@googlemaps/js-api-loader';
import GoogleMaps from '@becurious/googlemaps';

const props = defineProps({
	items: { type: Array, default: () => [] },
});

const config = useRuntimeConfig();
const gmap = ref(null);
const activeCategory = ref(null);
const allHighlights = ref(null);

let map = null;
const mapData = {};

const mapConfig = {
	zoom: 12,
	center: { lat: 52.3677534, lng: 4.8656398 },
	// prettier-ignore
	styles: [{ featureType: 'administrative', elementType: 'all', stylers: [{ visibility: 'off' }] }, { featureType: 'landscape', elementType: 'all', stylers: [ { visibility: 'simplified' }, { hue: '#0066ff' }, { saturation: 74 }, { lightness: 100 }]}, { featureType: 'poi', elementType: 'all', stylers: [{ visibility: 'off' }] }, { featureType: 'road', elementType: 'all', stylers: [{ visibility: 'simplified' }] }, { featureType: 'road.highway', elementType: 'all', stylers: [{ visibility: 'off' }, { weight: 0.6 }, { saturation: -85 }, { lightness: 61 }] }, { featureType: 'road.highway', elementType: 'geometry', stylers: [{ visibility: 'on' }] }, { featureType: 'road.arterial', elementType: 'all', stylers: [{ visibility: 'off' }] }, { featureType: 'road.local', elementType: 'all', stylers: [{ visibility: 'on' }] }, { featureType: 'transit', elementType: 'all', stylers: [{ visibility: 'simplified' }] }, { featureType: 'water', elementType: 'all', stylers: [ { visibility: 'simplified' }, { color: '#5f94ff' }, { lightness: 26 }, { gamma: 5.86 }]}],
	draggable: true,
	mapTypeControl: false,
	zoomControl: true,
	streetViewControl: true,
	scrollwheel: true,
};

watchEffect(() => {
	allHighlights.value = props.items.map((category) => category.items).flat();
});

const activeHighlights = computed(() =>
	allHighlights.value.filter((item) => {
		return parseInt(item.highlightsCategorie.ID, 10) === parseInt(activeCategory.value, 10);
	}),
);

onMounted(async () => {
	const google = await new Loader({
		apiKey: config.public.maps.apiKey,
	}).load();
	map = new GoogleMaps(google, gmap.value, mapConfig);
	initializeMap();
});

const initializeMap = () => {
	map.setMarkerIcon({
		src: '/images/marker.png',
		width: 40,
		height: 40,
		anchorX: 20,
		anchorY: 40,
	});

	map.addInfoWindow(
		map.addMarker(Object.values(mapConfig.center), 'Hotel de Hallen'),
		'<h3>Hotel de Hallen</h3>' + '<p class="address">Bellamyplein 47<br>1053 AT Amsterdam</p>',
	);
};

const updateMarkers = (id) => {
	if (!map) {
		return;
	}

	if (id === activeCategory.value) {
		activeCategory.value = null;
	} else {
		activeCategory.value = id;
	}

	map.setMarkerIcon({
		src: '/images/marker-item.png',
		width: 30,
		height: 42,
		anchorX: 15,
		anchorY: 42,
	});

	map.hideMarker('allcategory');

	activeHighlights.value.forEach((highlight) => {
		const marker = map.addMarker(
			[highlight.location.latitude, highlight.location.longitude],
			highlight.header,
			`cat_${highlight.category}`,
		);

		const infowindow = map.addInfoWindow(marker, getInfowindowContent(highlight));

		mapData[`#huid_${highlight.ID}`] = [infowindow, marker];
	});

	if (activeCategory.value) {
		map.autoCenter();
	}
};

const openInfowindow = (event) => {
	const ID = event.target.dataset.id;

	if (ID) {
		const currentMapData = mapData[event.target.dataset.id];
		map.openInfowindow(currentMapData[0], currentMapData[1]);
	}
};

const getInfowindowContent = (highlight) => {
	let html = '<h3>' + highlight.header + '</h3>';

	html += `<p class="address">
			${highlight.location.address}<br>${highlight.location.postalCode} ${highlight.location.city}
			</p>`;

	if (highlight.phonenumber) {
		html += `<p class="phone">${highlight.phonenumber}</p>`;
	}

	if (highlight.distance_public) {
		html += `<p class="directions public"><span class="icon-directions"></span>
				<span>${highlight.distancePublic}</span>
				</p>`;
	}

	if (highlight.distance_walk) {
		html += `<p class="directions walk"><span class="icon-directions"></span>
				<span>${highlight.distanceWalk}</span>
				</p>`;
	}

	if (highlight.url) {
		html += `<p class="website">
				<a href="${highlight.url}" target="_blank" class="highlight-button" rel="noopener noreferrer"><span>${highlight.button}</span></a>
				</p>`;
	}

	return html;
};
</script>

<style lang="scss" scoped>
.highlights-nav {
	padding-right: 0;

	ul {
		margin: 10px 0 20px;
		list-style: none;
		padding: 5px 30px;

		li {
			margin: 15px 0;

			span {
				cursor: pointer;
				font-size: 18px;
			}
		}
	}
}

:deep(.highlight-button) {
	background: var(--cta-color);
	color: var(--body-color);
	text-transform: uppercase;
	padding: 14px 26px;
	border-radius: 20px;

	&::after {
		display: none;
	}

	&:hover {
		background: var(--cta-color-hover);
	}
}

.highlight-category {
	border-radius: 6px;
	overflow: hidden;
	margin: 0 0 20px;
	background: var(--highlights-background-color);
}

.button-toggle {
	padding: 20px;
	transition: all 0.3s ease-in-out;
	width: 100%;
	text-align: left;
	background: var(--cta-color-blue);
	color: #fff;
	cursor: pointer;
	margin: 0;
	font-size: 14px;
	position: relative;
	display: flex;
	align-items: center;
	justify-content: space-between;
	text-transform: uppercase;
	font-weight: 900;

	&:hover,
	&.active {
		background: var(--cta-color-blue);
		color: #fff;
	}
}

.expand-icon {
	color: #fff;
	font-size: 20px;
	transition: all 0.3s ease-in-out;
}

.active {
	.expand-icon {
		transform: rotate(180deg);
	}
}

.maps {
	padding: 0 20px 20px;
	background: none;
}

#gmap {
	min-height: 600px;
	outline: none !important;

	&:focus {
		outline: none !important;
	}

	div {
		outline: none !important;
		border: none !important;

		&:focus {
			outline: none !important;
		}
	}
}

@media (max-width: 1080px) {
	.highlights-section .row {
		display: flex;
		flex-flow: column-reverse wrap;
	}

	.highlights-nav {
		width: 100%;
		padding: 0 20px;
	}

	ul.highlights li {
		width: auto;
		margin: 1% 1% 1% 0;
	}
}
</style>
